import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from "@src/constants/urls";

export const ICV5Agreement = ({ showSignature = true }) => (
  <div>
    <p style={{ textAlign: "center" }}>
      <strong>INDEPENDENT CONTRACTOR AGREEMENT</strong>
    </p>
    <p>
      This INDEPENDENT CONTRACTOR AGREEMENT (this “ICA”) is entered into by the contractor who has
      signed below or otherwise accepted this ICA (“you”) and Twomagnets LLC d/b/a Clipboard Health
      (“Clipboard”). You and Clipboard may be referred to, individually, as a “Party” and,
      collectively, as the “Parties.”
    </p>
    <p>
      This ICA, together with Clipboard’s{" "}
      <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noreferrer">
        Terms of Service
      </a>{" "}
      and{" "}
      <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">
        Privacy Policy
      </a>{" "}
      (collectively with this ICA, this “Agreement”), governs your access to Clipboard’s technology
      platform and applications (collectively, the “Marketplace”) that, among other things, may
      allow you to view and sign up to work certain shifts posted at workplaces (each, a
      “Workplace”) owned and/or operated by Clipboard’s third-party clients (each, a “Client”), as
      well as any services that you provide to those Clients (“Contracted Services”).
    </p>
    <p>
      <strong>
        PLEASE REVIEW THIS AGREEMENT CAREFULLY. IN PARTICULAR, YOUR ATTENTION IS DIRECTED TO THE
        MUTUAL ARBITRATION PROVISION IN SECTION 12, AS IT REQUIRES THE PARTIES TO RESOLVE DISPUTES
        ON AN INDIVIDUAL BASIS, TO THE FULLEST EXTENT PERMITTED BY LAW, THROUGH FINAL AND BINDING
        ARBITRATION.
      </strong>
    </p>
    <p>
      <strong>
        BY SIGNING OR OTHERWISE ACCEPTING THIS ICA, YOU ACKNOWLEDGE THAT YOU HAVE READ AND
        UNDERSTOOD ALL OF THE TERMS, INCLUDING SECTION 12, AND HAVE TAKEN THE TIME AND SOUGHT ANY
        ASSISTANCE NEEDED TO COMPREHEND THE CONSEQUENCES OF ACCEPTING THIS AGREEMENT.
      </strong>
    </p>
    <ol>
      <li>
        <p>
          <span>Relationship of the Parties.</span>
        </p>
        <ol>
          <li>
            <p>
              You are an independent contractor, and this Agreement shall not be construed to create
              any association, partnership, joint venture, employment, or agency relationship
              between you and Clipboard or any Client, for any purpose. You have no authority (and
              shall not hold yourself out as having authority) to make or accept any agreements or
              representations on behalf of Clipboard or any Client.
            </p>
          </li>
          <li>
            <p>
              Without limiting Section 1(a), you shall not be eligible to participate in any
              vacation, group medical or life insurance, disability, profit sharing or retirement
              benefits, or any other fringe benefits or benefit plans offered by Clipboard to its
              employees, and Clipboard will not be responsible for withholding or paying any income,
              payroll, Social Security, or other federal, state, or local taxes, making any
              insurance contributions, including for unemployment or disability, or obtaining
              workers’ compensation insurance on your behalf. You shall be responsible for, and
              shall indemnify Clipboard against liability for any such taxes or contributions that
              you fail to pay, including penalties and interest.
            </p>
          </li>
          <li>
            <p>
              You acknowledge and agree that: (i) the Parties operate separate and distinct business
              enterprises that each provides a service outside the usual course of business of the
              other; (ii) you are customarily engaged in an independently established trade,
              occupation, or business of the same nature as any Contracted Services that you provide
              to Clients under this Agreement, and plan to continue to engage in that trade,
              occupation, or business regardless of whether this Agreement remains in effect; and
              (iii) this Agreement and your use of the Marketplace shall not prevent you from
              engaging in any other business trade, commercial activity, or provision of services to
              any third party.
            </p>
          </li>
        </ol>
      </li>
      <li>
        <p>
          <span>Viewing and Booking Shifts.</span>
        </p>
        <ol>
          <li>
            <p>
              Clipboard will endeavor to make the Marketplace accessible to you, and, subject to
              Section 2(b), below, present you with open shifts, if any, that match the
              qualifications you share with Clipboard. You are not required to book any particular
              shift available through the Marketplace, or any shifts at all, and you acknowledge and
              agree that there will be no consequences for your decision not to do so.
            </p>
          </li>
          <li>
            <p>
              Clipboard has sole discretion as to which, if any, shifts to present to you. You
              further acknowledge and agree that Clipboard, in the exercise of its discretion, may
              honor do-not-return requests from Clients which could result in shifts being hidden
              from you or otherwise made unavailable.
            </p>
          </li>
          <li>
            <p>
              To the extent that you book a shift presented to you through the Marketplace, you
              authorize Clipboard to communicate with both you and the corresponding Client about
              the shift and to establish and facilitate direct communication between you and that
              Client.{" "}
            </p>
          </li>
        </ol>
      </li>
      <li>
        <p>
          <span>Performance of Contracted Services.</span>
        </p>
        <ol>
          <li>
            <p>
              Clipboard shall not control or direct the manner or means by which you perform any
              Contracted Services, including but not limited to, the time, place, or manner in which
              you perform such services for Clients, which will be based solely on your independent
              professional judgment.
            </p>
          </li>
          <li>
            <p>
              You shall furnish, at your own expense, the materials, equipment, supplies, and other
              resources necessary to perform any Contracted Services, except to the extent that a
              Client, in its sole discretion, elects to furnish any of the foregoing.
            </p>
          </li>
          <li>
            <p>
              You shall comply with all lawful third-party access rules and procedures communicated
              to you in writing by Clients or on their behalf, including those related to safety,
              security, and confidentiality. You acknowledge and agree that any such rules and
              procedures are determined solely by Clients, not by Clipboard.
            </p>
          </li>
        </ol>
      </li>
      <li>
        <p>
          <span>Payment.</span>
        </p>
        <ol>
          <li>
            <p>
              In consideration of performing Contracted Services for Clients, you shall receive the
              pay stated in the Marketplace listing for each shift that you work. You acknowledge
              that you shall be solely responsible for all applicable federal, state, and local
              taxes, as set out in Section 1(b), above.
            </p>
          </li>
          <li>
            <p>
              Unless otherwise expressly agreed to between you and any Client for which you perform
              Contracted Services, you are solely responsible for any travel or other costs or
              expenses incurred by you in connection with the performance of such services, and in
              no event shall Clipboard reimburse you for any such costs or expenses.
            </p>
          </li>
          <li>
            <p>
              Clipboard will make any payments due to you for working a shift under this Agreement
              within seven (7) days of receiving confirmation from you and the corresponding Client,
              to the reasonable satisfaction of Clipboard, that you worked the shift. Payments will
              be made using the Stripe platform, or by such other means as Clipboard may, in its
              reasonable discretion, specify. You acknowledge and agree that: (i) receiving payment
              from Clipboard may require you to open an account with Stripe; (ii) Stripe may impose
              additional terms, conditions, and/or fees in connection with the opening and
              maintenance of such accounts; and (iii) Clipboard and/or Stripe may impose additional
              terms, conditions, and/or fees in connection with the expedited withdrawal of funds
              from your Stripe account.
            </p>
          </li>
        </ol>
      </li>
      <li>
        <p>
          <span>Term and Termination.</span>
        </p>
        <ol>
          <li>
            <p>
              The term of this Agreement shall commence on the date you sign or otherwise accept it
              and shall continue until terminated as provided herein (the “Term”).
            </p>
          </li>
          <li>
            <p>
              You acknowledge and agree that Clipboard has no obligation to provide, or continue to
              provide, the Marketplace in any specific location or territory. Clipboard reserves the
              right to, in its sole discretion, suspend or terminate your access to the Marketplace
              at any time or for any reason. Likewise, you may suspend or terminate your use of the
              Marketplace at any time and for any reason. If you wish to deactivate your account,
              please contact Clipboard’s customer support.
            </p>
          </li>
          <li>
            <p>
              The following provisions shall survive termination of this Agreement: Section 9
              (Confidentiality), Section 11 (Indemnification), Section 12 (Mutual Arbitration),
              Section 13 (Governing Law, Jurisdiction, and Venue), and Section 14 (Miscellaneous).
            </p>
          </li>
        </ol>
      </li>
      <li>
        <p>
          <span>Your Representations, Warranties, and Covenants.</span> You represent, warrant, and
          covenant to Clipboard that:
        </p>
        <ol>
          <li>
            <p>
              you have the right to enter into this Agreement, to grant the rights granted in this
              Agreement, and to perform fully all of your obligations in this Agreement;
            </p>
          </li>
          <li>
            <p>
              your entering into this Agreement with Clipboard and your performance of Contracted
              Services as contemplated herein does not and will not conflict with or result in any
              breach or default under any other agreement to which you are subject;
            </p>
          </li>
          <li>
            <p>
              you have the skill, experience, and qualifications, including, without limitation, all
              licenses, permits, and registrations required to perform any Contracted Services you
              undertake to render pursuant to this Agreement;
            </p>
          </li>
          <li>
            <p>all information that you provide to Clipboard will be true and accurate;</p>
          </li>
          <li>
            <p>
              you will promptly notify Clipboard in writing if: (i) any information you have
              provided Clipboard becomes inaccurate or requires updating; (ii) you become subject to
              any disciplinary and/or revocation proceeding related to any license, permit, or
              registration required to perform any Contracted Services you undertake to render
              pursuant to this Agreement, or if any such license, permit, or registration expires,
              is revoked, is abandoned, or otherwise becomes inoperative; or (iii) you are charged
              with or convicted of any misdemeanor or felony involving fraud, controlled substances,
              moral turpitude, or any act of physical violence; and
            </p>
          </li>
          <li>
            <p>
              you, and not Clipboard, shall be responsible for maintaining books and records related
              to your separate and distinct business enterprise, including, without limitation, any
              Contracted Services that you perform.
            </p>
          </li>
        </ol>
      </li>
      <li>
        <p>
          <span>Clipboard’s Representations and Warranties.</span> Clipboard represents and warrants
          to you that:
        </p>
        <ol>
          <li>
            <p>
              it has the full right, power, and authority to enter into this Agreement and to
              perform its obligations hereunder; and
            </p>
          </li>
          <li>
            <p>
              it been duly authorized by all necessary corporate action to enter into this
              Agreement.
            </p>
          </li>
        </ol>
      </li>
      <li>
        <p>
          <span>Intellectual Property Rights and Licenses.</span>
        </p>
        <ol>
          <li>
            <p>
              All copyright, database rights, trademarks (whether registered or unregistered),
              design rights (whether registered or unregistered), patent applications, patents, and
              other intellectual property rights of any nature in the Marketplace, together with the
              underlying software code and any and all rights in, or derived from the Marketplace
              are proprietary and owned either directly by Clipboard or by Clipboard’s licensors and
              are protected by applicable intellectual property and other laws. You agree that you
              will not use such proprietary information, materials, or intellectual property rights
              in any way whatsoever except for use of the Marketplace in accordance with the terms
              of this Agreement. No portion of the Marketplace may be reproduced in any form or by
              any means, and you agree not to modify, rent, lease, loan, sell, distribute, exploit,
              or create derivative works based on the Marketplace or its contents.
            </p>
          </li>
          <li>
            <p>
              You acknowledge and agree that any questions, comments, suggestions, ideas, feedback,
              or other information (collectively, “Submissions”) that you provide to Clipboard
              regarding the Marketplace or any Client are provided freely and shall become the sole
              property of Clipboard. Clipboard shall own exclusive rights of such Submissions,
              including all intellectual property rights therein, and shall be entitled to the
              unrestricted use and dissemination of these Submissions for any purpose, commercial or
              otherwise, without acknowledgment or compensation to you.
            </p>
          </li>
        </ol>
      </li>
      <li>
        <p>
          <span>Confidentiality.</span>
        </p>
        <ol>
          <li>
            <p>
              You acknowledge and agree that you may have access to information that is treated as
              confidential and proprietary by Clipboard, its affiliates, and its Clients, including,
              without limitation, trade secrets, technology, and information pertaining to business
              operations and strategies, customers, pricing, marketing, finances, sourcing,
              personnel, patients, or operations, in each case whether spoken, written, printed,
              electronic, or in any other form or medium (collectively, the “Confidential
              Information”).
            </p>
          </li>
          <li>
            <p>
              Subject to Section 9(d) and Section 9(e), below, you agree to treat all Confidential
              Information as strictly confidential, not to disclose Confidential Information or
              permit it to be disclosed, in whole or part, to any third party without the prior
              written consent of Clipboard and the applicable Client in each instance, and not to
              use any Confidential Information for any purpose except as required in the performance
              of the Contracted Services pursuant to this Agreement. You shall notify Clipboard
              immediately in the event you become aware of any loss or unauthorized disclosure of
              any Confidential Information.
            </p>
          </li>
          <li>
            <p>
              You acknowledge and agree you are required to abide by all local, state, and federal
              regulations concerning the confidentiality of records and individually identifiable
              health information to the extent applicable, including, without limitation, the Health
              Insurance Portability and Accountability Act (HIPAA), the Health Information
              Technology for Economic and Clinical Health (HITECH), and the American Recovery and
              Reinvestment Act (ARRA). Violation of this provision will constitute a material breach
              of this Agreement.
            </p>
          </li>
          <li>
            <p>
              Nothing in this Agreement shall be construed to prevent disclosure of Confidential
              Information as required by applicable law or regulation, or pursuant to the valid
              order of a court of competent jurisdiction or an authorized government agency,
              provided that the disclosure does not exceed the extent of disclosure required by such
              law, regulation, or order. You agree to provide written notice of any such order, or
              any proceeding that could reasonably result in such an order, to Clipboard within two
              (2) business days of receiving such order, but in any event sufficiently in advance of
              making any disclosure to permit Clipboard to contest the order or seek confidentiality
              protections, as determined in Clipboard’s sole discretion.
            </p>
          </li>
          <li>
            <p>
              <span>Notice of Immunity Under the Defend Trade Secrets Act of 2016 (DTSA).</span>{" "}
              Notwithstanding any other provision of this Agreement, you will not be held criminally
              or civilly liable under any federal or state trade secret law for any disclosure of a
              trade secret that: (i) is made: (A) in confidence to a federal, state, or local
              government official, either directly or indirectly, or to an attorney; and (B) solely
              for the purpose of reporting or investigating a suspected violation of law; or (ii) is
              made in a complaint or other document that is filed under seal in a lawsuit or other
              proceeding. If you file a lawsuit alleging retaliation by Clipboard for reporting a
              suspected violation of law, you may disclose Clipboard’s trade secrets to your
              attorney and use the trade secret information in the court proceeding if you file any
              document containing the trade secret under seal and do not disclose the trade secret,
              except pursuant to court order.
            </p>
          </li>
        </ol>
      </li>
      <li>
        <p>
          <span>Insurance.</span>
        </p>
        <ol>
          <li>
            <p>
              Throughout the Term, you shall maintain, at your own expense, current insurance, in
              amounts and of types required by law to provide any Contracted Services and cover you
              during your performance of such services. You further agree to provide to Clipboard,
              upon request, current certificates of insurance documenting any coverage required by
              this Section 10(a), and to promptly notify Clipboard in writing if any insurance
              policy providing such coverage expires, is canceled, or is not renewed, or if the
              policy’s underwriter informs you of its intent to cancel or not renew the policy.
            </p>
          </li>
          <li>
            <p>
              You agree to maintain sufficient insurance to cover any risks or claims arising out of
              or related to your relationship with Clipboard, including workers’ compensation
              insurance where required by law. You acknowledge and understand that you will not be
              eligible for workers’ compensation benefits through Clipboard and are instead
              responsible for maintaining your own workers’ compensation insurance or occupational
              accident insurance. Maintenance of your own workers’ compensation insurance or
              occupational accident insurance will not disqualify you from participating in any
              occupational accident insurance policy, which Clipboard, in its sole discretion, may
              make available to you.
            </p>
          </li>
        </ol>
      </li>
      <li>
        <p>
          <span>Indemnification.</span> You shall defend, indemnify, and hold harmless Clipboard and
          its affiliates, together their respective equity holders, officers, directors, employees,
          agents, successors, and assigns from and against all losses, damages, liabilities,
          deficiencies, actions, judgments, interest, awards, penalties, fines, costs, or expenses
          of whatever kind (including reasonable attorneys’ fees) arising out of or resulting from:
          (a) your performance of any Contracted Services, including, without limitation, bodily
          injury or death of any person, or damage to real or tangible personal property resulting
          from your acts or omissions; or (b) your breach of any representation, warranty, covenant,
          or obligation under this Agreement.
        </p>
      </li>
      <li>
        <p>
          <span>Mutual Arbitration.</span>
        </p>
        <ol>
          <li>
            <p>
              <span>Agreement to Arbitrate.</span>{" "}
              <strong>
                You and Clipboard agree that, except as provided below, any dispute, controversy, or
                claim arising, or that has arisen, out of or relating in any way to this Agreement,
                any breach or termination of this Agreement, your relationship with Clipboard,
                and/or any Contracted Services you provide under this Agreement (a “Claim”) will be
                determined by binding arbitration or in small claims court.
              </strong>{" "}
              This agreement to arbitrate does not cover any claims that cannot be compelled to
              arbitration as a matter of applicable law, including certain administrative claims
              with local, state, or federal government agencies. In addition, nothing in this
              Agreement shall be deemed to excuse a Party from bringing an administrative claim
              before any agency in order to fulfill the Party’s obligation to exhaust administrative
              remedies before making a claim in arbitration. Notwithstanding the above, to the
              extent a Party wishes to pursue a Claim after the administrative proceedings have
              concluded (or wishes to appeal an administrative decision), and those further
              proceedings would ordinarily be heard in a court, the Party must resolve those further
              proceedings through arbitration or in small claims court under this Agreement, and not
              in state or federal court. The Parties understand and fully agree that by entering
              into this Agreement to arbitrate they are giving up their right to have a trial by
              jury, and are giving up their normal rights of appeal following the rendering of a
              decision except as applicable law provides for judicial review of arbitration
              proceedings. The Parties anticipate that by entering into this Agreement, they will
              gain the benefits of a speedy and less expensive dispute resolution procedure. The
              Parties agree that the U.S. Federal Arbitration Act governs the interpretation and
              enforcement of this arbitration provision. This arbitration provision shall survive
              termination of this Agreement and/or the termination of your relationship with
              Clipboard.
            </p>
          </li>
          <li>
            <p>
              <span>Class, Collective, and Representative Action Waiver.</span>{" "}
              <strong>
                The Parties agree that each may bring Claims against the other only in your or its
                individual capacity and not as a plaintiff or class member in any purported class,
                collective, consolidated or representative proceeding, except as otherwise provided
                under applicable law.
              </strong>{" "}
              The Parties waive the right to assert, participate in, or receive money or any other
              relief from any class, representative, or collective claims against each other in
              court, arbitration, or any other proceeding. Each Party shall only submit their own
              individual claims against the other and will not seek to represent the interests of
              any other person. The Arbitrator shall have no jurisdiction or authority to adjudicate
              any class, representative, or collective claim, or to consolidate different
              arbitration proceedings with or join any other Party to an arbitration between the
              Parties. To the extent that the Arbitrator finds that any Claim is non-arbitrable or
              must proceed on a class, collective, consolidated, or representative basis, that Claim
              must be litigated in a civil court of competent jurisdiction and not in arbitration,
              and the Parties agree that litigation of those Claims shall be stayed, and any
              applicable statutes of limitations tolled, pending the outcome of any individual
              Claims in arbitration.
            </p>
          </li>
          <li>
            <p>
              <span>Notice of Claim.</span> Before electing to seek arbitration or filing in small
              claims court, a Party must give the other Party written notice of any Claim within the
              applicable statute of limitations for the type of Claim(s) asserted (the “Notice of
              Claim”). Any Notice of Claim provided to Clipboard by you must be sent to Clipboard
              Health, 440 N. Barranca Ave. #5028 Covina, CA 91723. Any Notice of Claim provided to
              you by Clipboard will be sent to your current residential address on file with
              Clipboard. The Notice of Claim must identify and describe the nature of all Claims
              asserted and the facts upon which such Claims are based. The Parties agree that, after
              a Notice of Claim is sent but before either you or Clipboard initiate arbitration or
              file a claim in small claims court against the other, we will personally meet, via
              telephone or videoconference, in a good-faith effort to confer with each other and try
              to resolve informally any Claim covered by this Agreement. You agree to participate in
              the conference personally, and Clipboard agrees to have a company representative
              participate in the conference. If either Party is represented by counsel with respect
              to any Notice of Claim, that counsel may participate in the conference as well. The
              statute of limitations and any filing fee deadlines shall be tolled while the Parties
              engage in the informal dispute resolution process required by this paragraph. If the
              Parties do not reach an agreement to resolve the Claim within sixty (60) days after
              the Notice of Claim is received, you or Clipboard may commence an arbitration
              proceeding by filing a Demand for Arbitration or, alternatively, by filing a Claim in
              small claims court. You agree that you may not commence any arbitration or file a
              Claim in small claims court unless you have complied with this provision and made a
              good faith effort to resolve your Claim directly with Clipboard during that time.
            </p>
          </li>
          <li>
            <p>
              <span>Arbitration Procedures.</span> The arbitration will be conducted by the AAA
              before a single AAA Arbitrator under the AAA’s rules, which are available at
              www.adr.org, except as modified by this Agreement or otherwise agreed between you and
              Clipboard. Arbitration shall be held in the county in which you have contracted to
              provide Contracted Services under this Agreement before a single Arbitrator. The
              Arbitrator will be selected using the following procedure: (a) the AAA will send the
              Parties a list of five candidates; (b) if the Parties cannot agree on an arbitrator
              from the list, each Party shall return its list to the AAA within 10 days, striking up
              to two candidates, and ranking the remaining candidates in order of preference; (c)
              the AAA shall appoint as Arbitrator the candidate with the highest aggregate ranking;
              and (d) if for any reason the appointment cannot be made according to this procedure,
              the AAA may exercise its discretion in appointing the Arbitrator. The Arbitrator shall
              have exclusive authority to resolve any dispute relating to the validity,
              enforceability, or scope of this Agreement and the arbitrability of any dispute
              between the Parties. The Arbitrator shall provide the Parties with a reasoned written
              decision explaining his or her findings and conclusions. Any arbitral award
              determination shall be final and binding upon the Parties and subject to judicial
              review pursuant to the law applicable to judicial review of arbitration proceedings.
              The award shall be binding only among the Parties and shall have no preclusive effect
              in any other arbitration or other proceeding involving a different Party. Clipboard
              will not seek to recover its attorneys’ fees and costs in arbitration from you unless
              the Arbitrator finds that either the substance of your Claim or the relief sought in
              your Demand for Arbitration was frivolous or was brought for an improper purpose (as
              measured by the standards set forth in Federal Rule of Civil Procedure 11(b)).
              Judgment on the Arbitrator’s award may be entered in any court of competent
              jurisdiction.
            </p>
          </li>
          <li>
            <p>
              <span>Small Claims Option.</span> Either you or Clipboard can seek to have a Claim
              resolved in small claims court if all the requirements of the small claims court are
              satisfied. Either you or Clipboard may seek to have a Claim resolved in small claims
              court in the county in which you have contracted to provide Contracted Services under
              this Agreement, and you may also bring a Claim in small claims court in the Superior
              Court of California, County of San Francisco. If a Claim qualifies for small claims
              court, but a Party commences an arbitration proceeding, you and Clipboard agree that
              either Party may elect instead to have the Claim resolved in small claims court, and
              upon written notice of a Party’s election, the American Arbitration Association
              (“AAA”) will administratively close the arbitration proceeding. Any dispute about
              whether a Claim qualifies for small claims court shall be resolved by that court, not
              by an arbitrator.
            </p>
          </li>
          <li>
            <p>
              <span>Relief Available in Arbitration.</span> Unless you or Clipboard seek to have a
              Claim resolved in small claims court, the Arbitrator shall determine all issues of
              liability on the merits of any Claim asserted by you or Clipboard and may award
              declaratory or injunctive relief only in favor of the individual Party seeking relief
              and only to the extent necessary to provide relief warranted by that Party’s
              individual Claim. To the extent that either Party prevails on a Claim and seeks public
              injunctive relief (that is, injunctive relief that has the primary purpose and effect
              of prohibiting unlawful acts that threaten future injury to the public), the
              entitlement to and extent of such relief must be litigated in a civil court of
              competent jurisdiction and not in arbitration. The Parties agree that litigation of
              any issues of public injunctive relief shall be stayed pending the outcome of the
              merits of any individual Claim in arbitration. Before a court of competent
              jurisdiction issues any public injunctive relief, it shall review the factual findings
              of the arbitration award on which any injunction would issue with no deference to the
              Arbitrator.
            </p>
          </li>
          <li>
            <p>
              <span>Arbitration Fees.</span> Payment of all filing, administration, and arbitrator
              fees will be governed by the AAA Rules. You are required to pay the AAA’s initial
              filing fee, but Clipboard will reimburse you for this filing fee at the conclusion of
              the arbitration to the extent it exceeds the fee that you would have to pay to file a
              complaint in federal or state court in the county in which you have contracted to
              provide Contracted Services under this Agreement or in San Francisco County,
              California, whichever amount is greater. If the Arbitrator finds that either your
              Claim or the relief sought in your Demand for Arbitration was frivolous, was brought
              for an improper purpose, or otherwise violates the standards set forth in Federal Rule
              of Civil Procedure 11(b), then the payment of all fees will be governed by the AAA
              Rules and Clipboard will not reimburse your initial filing fee. The Parties agree that
              the AAA has discretion to modify the amount or timing of any administrative or
              arbitration fees due under the AAA Rules where it deems appropriate, provided that
              such modification does not increase the AAA fees to you or Clipboard and you and
              Clipboard waive any objection to such fee modification.
            </p>
          </li>
          <li>
            <p>
              <span>Effect of Unenforceability or Inapplicability.</span> If any portion of this
              Section 12 is found to be unenforceable or unlawful in any respect or for any reason,
              or with respect to any particular Claim, then the relevant portion of this Section 12
              shall be severed from this Agreement. Any such severance shall have no impact
              whatsoever on the remainder of this Section 12, and this Agreement will still be fully
              enforceable as to any and all Claims, which must be resolved in arbitration, unless a
              Party elects the small claims option under Section 12(e). If Section 12 in its
              entirety is found to be unenforceable, unlawful, or inapplicable for any reason, or
              with respect to any particular Claim, then the subject dispute or Claim shall be
              governed by the agreement to arbitrate in the most recent prior Independent Contractor
              Agreement between the Parties, to the extent one exists, notwithstanding Section
              14(c), below.
            </p>
          </li>
        </ol>
      </li>
      <li>
        <p>
          <span>Governing Law, Jurisdiction, and Venue.</span>
        </p>
        <ol>
          <li>
            <p>
              To the fullest extent allowable by law, this Agreement and all related documents,
              including all schedules attached and all matters arising out of or relating to this
              Agreement and any Contracted Services provided hereunder, whether sounding in
              contract, tort, or statute, are governed by, and construed in accordance with, the
              laws of the State of Delaware without regard to its conflict of laws rules.
            </p>
          </li>
          <li>
            <p>
              Notwithstanding the foregoing, the dispute resolution process set forth in Section 12,
              above, shall be governed by the Federal Arbitration Act.
            </p>
          </li>
          <li>
            <p>
              Except where prohibited by law and except for claims that are heard in a small claims
              court as set forth in Section 12, above, any claims arising out of or relating in any
              way to this Agreement that for whatever reason are not required to be arbitrated or
              filed in small claims court will be litigated exclusively in the state or federal
              court in San Francisco, California. You and Clipboard all consent to venue and
              personal jurisdiction in San Francisco, California, and waive any claim that such
              courts constitute an inconvenient forum.
            </p>
          </li>
        </ol>
      </li>
      <li>
        <p>
          <span>Miscellaneous.</span>
        </p>
        <ol>
          <li>
            <p>
              You shall not export, directly or indirectly, any technical data acquired from
              Clipboard, or any products utilizing any such data, to any country in violation of any
              applicable export laws or regulations.
            </p>
          </li>
          <li>
            <p>
              Except as explicitly stated otherwise, any notices to Clipboard shall be given by
              certified mail, postage prepaid and return receipt requested to Clipboard Health, 440
              N. Barranca Ave. #5028 Covina, CA 91723, Attn: Legal Department. All notices to you
              may be given electronically through the Marketplace and/or at the e-mail address you
              provide to Clipboard.
            </p>
          </li>
          <li>
            <p>
              This Agreement, together with any other documents incorporated by reference,
              constitute the sole and entire agreement of the Parties to this Agreement with respect
              to the subject matter contained herein, and, except as otherwise provided in Section
              12(h), above, supersede all prior and contemporaneous understandings, agreements,
              representations, and warranties, both written and oral, with respect to such subject
              matter.
            </p>
          </li>
          <li>
            <p>
              Clipboard may modify this Agreement at any time. If Clipboard makes changes to this
              Agreement, it will provide you with notice of those changes before the revised
              Agreement becomes effective. If you disagree with the revised Agreement, you may
              terminate the Agreement immediately as provided herein. If you do not terminate the
              Agreement before the date the revised Agreement becomes effective, your continued use
              of the Marketplace and/or performance of Contracted Services under the revised
              Agreement will constitute acceptance of the revised Agreement.
            </p>
          </li>
          <li>
            <p>
              Clipboard may freely assign its rights and obligations under this Agreement at any
              time by written notice to you. You may not assign this Agreement without the prior
              written consent of Clipboard, and any attempt to do so will be void. Subject to the
              limits on assignment stated above, this Agreement will inure to the benefit of, be
              binding on, and be enforceable against each of the Parties and their respective
              successors and permitted assigns.
            </p>
          </li>
          <li>
            <p>
              Except as otherwise provided in Section 12, above, if any term or provision of this
              Agreement is invalid, illegal, or unenforceable in any jurisdiction, such invalidity,
              illegality, or unenforceability shall not affect any other term or provision of this
              Agreement or invalidate or render unenforceable such term or provision in any other
              jurisdiction.
            </p>
          </li>
          <li>
            <p>
              No failure or delay on the part of either Party in exercising any right, power or
              remedy hereunder shall operate as a waiver thereof, nor shall any single or partial
              exercise of any such right, power or remedy preclude any other or further exercise
              thereof or the exercise of any other right, power or remedy.
            </p>
          </li>
          <li>
            <p>
              Without limiting the means by which it may be accepted, this Agreement may be executed
              in multiple counterparts and by electronic signature, each of which shall be deemed an
              original and all of which together shall constitute one instrument.
            </p>
          </li>
        </ol>
      </li>
    </ol>
    {showSignature ? (
      <>
        <p>Twomagnets LLC d/b/a Clipboard Health</p>
        <table data-testid={"ceo-signature"}>
          <tr>
            <td>By:</td>
            <td style={{ borderBottom: "1pt solid black" }}>
              /s/ <i>Wei Deng</i>
            </td>
          </tr>
          <tr>
            <td>Name:</td>
            <td>Wei Deng</td>
          </tr>
          <tr>
            <td>Title:</td>
            <td>Chief Executive Officer</td>
          </tr>
        </table>
      </>
    ) : null}
  </div>
);
